import { Controller } from "@hotwired/stimulus"

/**
 * Dropdown controller for mobile menu and other toggle elements
 *
 * Usage:
 * <div data-controller="dropdown">
 *   <button data-action="click->dropdown#toggle" data-dropdown-target="button">Menu</button>
 *   <div data-dropdown-target="menu" class="hidden">
 *     <!-- Menu content -->
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ["button", "menu"]
  static values = {
    open: { type: Boolean, default: false }
  }

  connect() {
    this.boundClickOutside = this.clickOutside.bind(this)
    this.boundKeydown = this.keydown.bind(this)

    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("keydown", this.boundKeydown)

    // Set accessibility attributes
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-haspopup", "true")
      this.buttonTarget.setAttribute("aria-expanded", this.openValue.toString())
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.openValue = true

    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove("hidden")
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", "true")
    }

    this.dispatch("opened")
  }

  close() {
    this.openValue = false

    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", "false")
    }

    this.dispatch("closed")
  }

  clickOutside(event) {
    if (!this.openValue) return
    if (this.element.contains(event.target)) return

    this.close()
  }

  keydown(event) {
    if (!this.openValue) return

    if (event.key === "Escape") {
      this.close()

      if (this.hasButtonTarget) {
        this.buttonTarget.focus()
      }
    }
  }

  openValueChanged() {
    // Hook for any additional logic when open state changes
  }
}
